// دریافت تنخواه تدارکات از ذیحسابی
// props: { user, onClose | onBack, programKind (0|1), year }
import React, { useState, useEffect, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const HZ = ['hazineh_id', 'hazineh_id1', 'hazineh_id2', 'hazineh_id3', 'hazineh_id4', 'hazineh_id5'];
const EMPTY = {
  factor_no: '', factor_date: '', receive_cash_pay: '', remark: '', buyer_id: 0, buyer: '', credit_id: 0, credit: '',
  program_id: 0, program: '', project_id: 0, project: '',
  hazineh_id: 0, hazineh_id1: 0, hazineh_id2: 0, hazineh_id3: 0, hazineh_id4: 0, hazineh_id5: 0,
  hz: ['', '', '', '', '', ''],
};

const gridCols = (lv) => [
  { key: 'Remark', label: 'شرح', wide: true }, { key: 'FactorNo', label: 'شماره' }, { key: 'FactorDate', label: 'تاریخ' },
  { key: 'ReceiveCashPay', label: 'مبلغ', money: true }, { key: 'buyer', label: 'کارپرداز' },
  { key: 'credit', label: 'منبع اعتبار', wide: true }, { key: 'program', label: 'برنامه', wide: true },
  { key: 'project', label: 'پروژه', wide: true },
  { key: 'hz0', label: lv[1] }, { key: 'hz1', label: lv[2] }, { key: 'hz2', label: lv[3] },
  { key: 'hz3', label: lv[4] }, { key: 'hz4', label: lv[5] }, { key: 'hz5', label: lv[6] },
];
const STAT_COLS = [
  { key: 'title', label: 'شرح' }, { key: 'recv', label: 'دریافتی تنخواه', money: true },
  { key: 'paid', label: 'پرداختی', money: true }, { key: 'remain', label: 'موجودی', money: true },
];

const emptyVal = v => v === null || v === undefined || v === '' || v === 0 || v === '0';

function Lookup({ value, disabled, fetcher, onPick, onClear }) {
  const [text, setText] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setText(value || ''); }, [value]);
  const search = (q) => {
    setText(q);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetcher(q).then(j => { setItems(j && j.ok ? j.data : []); setOpen(true); });
    }, 300);
  };
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: '160px' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <input style={ui.input} value={text} disabled={disabled}
          onChange={e => search(e.target.value)} onFocus={() => !disabled && search(text)}
          onBlur={() => setTimeout(() => setOpen(false), 200)} />
        {!disabled && value && <button type="button" style={ui.btnCancel} onClick={onClear}>✕</button>}
      </div>
      {open && items.length > 0 && (
        <div style={s.drop}>
          {items.map((it, i) => (
            <div key={i} style={s.dropItem} onMouseDown={() => { onPick(it); setOpen(false); }}>{toFa(it.display)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, width }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: width || 'auto', flex: width ? 'none' : 1 }}>
      <label style={ui.mlabel}>{label}</label>
      {children}
    </div>
  );
}

function Table({ cols, rows, selId, onSelect }) {
  return (
    <table style={ui.table}>
      <thead><tr>{cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length || 1} style={ui.empty}>موردی یافت نشد</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.HazinehDocumentDetailId ?? i} onClick={() => onSelect && onSelect(r)}
            style={{ background: selId && r.HazinehDocumentDetailId === selId ? '#e8f0fe' : (i % 2 ? '#f3e2c7' : '#fff'), cursor: 'pointer' }}>
            {cols.map(c => (
              <td key={c.key} style={{ ...(c.money ? ui.tdNum : ui.td), ...(c.wide ? { whiteSpace: 'normal', minWidth: '160px', textAlign: 'right' } : {}),
                ...(c.key === 'remain' && Number(r.remain) < 0 ? { color: '#c62828' } : {}) }}>
                {c.money ? fmtAmt(r[c.key]) : toFa(r[c.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function HazinehDocumentReceiveCash({ user, onClose, onBack, programKind = 0, year: initYear = '' }) {
  const kind = Number(programKind) === 1 ? 1 : 0;
  const back = onClose || onBack;
  const fb = useFormButtons('FRMHAZINEHDOCUMENTRECEIVECASH', ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL']);
  const [year, setYear] = useState(String(initYear || ''));
  const [search, setSearch] = useState('');
  const [levels, setLevels] = useState(['', '', '', '', '', '', '']);
  const [sel, setSel] = useState(null);
  const [stats, setStats] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [askDel, setAskDel] = useState(false);

  const L = usePagedList((page, o) => API(`/receive-cash/?page=${page}&program_kind=${kind}&year=${o.year || ''}&search=${encodeURIComponent(o.search || '')}`));
  const reload = (o = {}) => L.reload({ year, search, ...o }).then(j => {
    if (j && j.ok) {
      if (!year) setYear(String(j.year));
      setLevels(j.levels || levels);
      setSel(cur => (j.data || []).find(r => cur && r.HazinehDocumentDetailId === cur.HazinehDocumentDetailId) || (j.data || [])[0] || null);
    }
    return j;
  });
  useEffect(() => { reload(); }, [kind]);

  useEffect(() => {
    if (!sel) { setStats([]); return; }
    API(`/receive-cash/${sel.HazinehDocumentDetailId}/statistics/?program_kind=${kind}&year=${year}`)
      .then(j => setStats(j.ok ? j.data : []));
  }, [sel && sel.HazinehDocumentDetailId]);

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const say = (m) => setMsg(Array.isArray(m) ? m : [m]);

  const openNew = () => { setForm(EMPTY); setModal('new'); };
  const openEdit = () => {
    if (!sel) return;
    const r = sel;
    setForm({
      factor_no: r.FactorNo || '', factor_date: r.FactorDate || '', receive_cash_pay: r.ReceiveCashPay || '', remark: r.Remark || '',
      buyer_id: r.BuyerId || 0, buyer: r.buyer, credit_id: r.CreditId || 0, credit: r.credit,
      program_id: r.ProgramId || 0, program: r.program, project_id: r.ProjectId || 0, project: r.project,
      hazineh_id: r.HazinehId || 0, hazineh_id1: r.HazinehId1 || 0, hazineh_id2: r.HazinehId2 || 0,
      hazineh_id3: r.HazinehId3 || 0, hazineh_id4: r.HazinehId4 || 0, hazineh_id5: r.HazinehId5 || 0,
      hz: [r.hz0, r.hz1, r.hz2, r.hz3, r.hz4, r.hz5].map(x => x || ''),
    });
    setModal('edit');
  };

  // انتخاب یک سطح کد هزینه ⇒ سطوح بالاتر از سرور تکمیل می‌شود
  const pickHazineh = async (level, item) => {
    const ids = HZ.map((k, i) => (i === level ? (item ? item.id : 0) : form[k]));
    const texts = form.hz.map((t, i) => (i === level ? (item ? item.display : '') : t));
    const j = await API('/receive-cash/cascade/', { method: 'POST',
      body: JSON.stringify({ level, program_kind: kind, year, ids }) });
    if (!j.ok) { say(j.msg); return; }
    setForm(f => {
      const nf = { ...f };
      HZ.forEach((k, i) => { nf[k] = j.ids[i]; });
      nf.hz = j.texts.map((t, i) => t || (j.ids[i] ? texts[i] : ''));
      return nf;
    });
    if (j.messages && j.messages.length) say(j.messages);
  };
  const parentOf = (level) => {
    for (let i = level - 1; i >= 0; i -= 1) if (form[HZ[i]]) return form[HZ[i]];
    return 0;
  };

  const save = async () => {
    setSaving(true);
    const body = JSON.stringify({ ...form, receive_cash_pay: toEng(form.receive_cash_pay), year, program_kind: kind });
    const j = modal === 'new'
      ? await API('/receive-cash/create/', { method: 'POST', body })
      : await API(`/receive-cash/${sel.HazinehDocumentDetailId}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (!j.ok) { say(j.msg); return; }
    setModal(null);
    reload();
  };
  const doDelete = async () => {
    setAskDel(false);
    const j = await API(`/receive-cash/${sel.HazinehDocumentDetailId}/delete/`, { method: 'DELETE' });
    if (!j.ok) { say(j.msg); return; }
    setSel(null); reload();
  };

  const open = fb.loaded && fb.open;
  const cols = gridCols(levels).filter(c => L.data.some(r => !emptyVal(r[c.key])));
  const title = 'دریافت تنخواه تدارکات از ذیحسابی';
  const enabled = (b) => ({ ...b, opacity: sel ? 1 : 0.4, cursor: sel ? 'pointer' : 'default' });
  const lk = (name, q, extra = '') => API(`/receive-cash/lookup/${name}/?q=${encodeURIComponent(q)}&program_kind=${kind}&year=${year}${extra}`);

  const toolbar = open && (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFa(year)} maxLength={4} style={ui.yearInput} onChange={e => {
          const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')).slice(0, 4);
          setYear(v); if (v.length === 4) reload({ year: v });
        }} />
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && reload({ search })}
        style={{ ...ui.searchInput, flex: 1, minWidth: '160px' }} placeholder="جستجو (شرح، کارپرداز، مبلغ)…" />
      <div style={ui.toolBtns}>
        {fb.can('BTNNEW') && <button style={{ ...ui.btnNew }} onClick={openNew}>＋ جدید</button>}
        {fb.can('BTNEDIT') && <button style={enabled(ui.btnEdit)} disabled={!sel} onClick={openEdit}>✎ ویرایش</button>}
        {fb.can('BTNDELETE') && <button style={enabled(ui.btnDel)} disabled={!sel} onClick={() => sel && setAskDel(true)}>✕ حذف</button>}
        <ExcelButton title={title} subtitle={`سال مالی ${toFa(year)}`} columns={cols} rows={L.data} />
      </div>
    </>
  );

  return (
    <ListPage user={user} onBack={back} title={title} subtitle="تدارکات — سامانه ارجان" toolbar={toolbar}
      error={L.error} pager={open ? L.pager : null} onMore={L.more} loading={L.loading} unit="ردیف">
      {open && (
        <div style={ui.splitWrap}>
          <div style={ui.topPane}>
            <Table cols={cols} rows={L.data} selId={sel && sel.HazinehDocumentDetailId} onSelect={setSel} />
          </div>
          <div style={ui.bottomPane}>
            <Table cols={STAT_COLS} rows={stats} />
          </div>
        </div>
      )}

      {modal && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '820px' }}>
            <div style={ui.mHeader}>
              <span style={ui.mTitle}>{modal === 'new' ? 'تنخواه دریافتی جدید' : 'ویرایش تنخواه دریافتی'}</span>
              <button style={ui.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={ui.mBody}>
              <div style={ui.row}>
                <Field label="شماره" width="140px"><input style={ui.input} value={form.factor_no} onChange={e => F('factor_no', e.target.value)} /></Field>
                <Field label="تاریخ " width="150px"><JalaliDatePicker value={form.factor_date} onChange={v => F('factor_date', v)} /></Field>
                <Field label="مبلغ" width="180px">
                  <input style={ui.input} value={form.receive_cash_pay ? Number(toEng(form.receive_cash_pay)).toLocaleString('fa-IR') : ''}
                    onChange={e => F('receive_cash_pay', toEng(e.target.value).replace(/[^0-9]/g, ''))} />
                </Field>
                <Field label="کارپرداز">
                  <Lookup value={form.buyer} fetcher={q => lk('buyer', q)}
                    onPick={it => setForm(f => ({ ...f, buyer_id: it.id, buyer: it.title }))}
                    onClear={() => setForm(f => ({ ...f, buyer_id: 0, buyer: '' }))} />
                </Field>
              </div>
              <div style={ui.row}>
                <Field label="شرح">
                  <Lookup value={form.remark} fetcher={q => (q ? lk('remark', q) : Promise.resolve({ ok: true, data: [] }))}
                    onPick={it => F('remark', it.title)} onClear={() => F('remark', '')} />
                </Field>
              </div>
              <div style={ui.row}>
                <Field label="منبع اعتبار">
                  <Lookup value={form.credit} fetcher={q => lk('credit', q)}
                    onPick={it => setForm(f => ({ ...f, credit_id: it.id, credit: it.display }))}
                    onClear={() => setForm(f => ({ ...f, credit_id: 0, credit: '' }))} />
                </Field>
                <Field label="برنامه/طرح">
                  <Lookup value={form.program} fetcher={q => lk('program', q)}
                    onPick={it => setForm(f => ({ ...f, program_id: it.id, program: it.display }))}
                    onClear={() => setForm(f => ({ ...f, program_id: 0, program: '' }))} />
                </Field>
                <Field label="پروژه">
                  <Lookup value={form.project} fetcher={q => lk('project', q, `&program_id=${form.program_id}`)}
                    onPick={it => setForm(f => ({ ...f, project_id: it.id, project: it.display }))}
                    onClear={() => setForm(f => ({ ...f, project_id: 0, project: '' }))} />
                </Field>
              </div>
              <div style={{ ...ui.row, flexDirection: 'column' }}>
                <label style={ui.mlabel}>کد هزینه</label>
                {HZ.map((k, i) => (
                  <Field key={k} label={levels[i + 1]}>
                    <Lookup value={form.hz[i]} fetcher={q => lk(`hazineh${i}`, q, `&parent=${parentOf(i)}`)}
                      onPick={it => pickHazineh(i, it)} onClear={() => pickHazineh(i, null)} />
                  </Field>
                ))}
              </div>
            </div>
            <div style={ui.mFooter}>
              {fb.can('BTNCANCEL') && <button style={ui.btnCancel} onClick={() => setModal(null)}>لغو</button>}
              {fb.can('BTNOK') && fb.can(modal === 'new' ? 'BTNNEW' : 'BTNEDIT') && (
                <button style={ui.btnSave} disabled={saving} onClick={save}>{saving ? '⏳' : 'ثبت'}</button>)}
            </div>
          </div>
        </div>
      )}

      {askDel && (
        <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر" tone="danger"
          onConfirm={doDelete} onCancel={() => setAskDel(false)} />
      )}
      {msg && <WarningDialog title="پیام" lines={msg} confirmLabel="تایید" cancelLabel="بستن" onConfirm={() => setMsg(null)} onCancel={() => setMsg(null)} />}
    </ListPage>
  );
}

const s = {
  drop: { position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto', zIndex: 1100, boxShadow: '0 6px 18px rgba(0,0,0,0.15)' },
  dropItem: { padding: '7px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3' },
};
